import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { TrendingUp, DollarSign, ArrowUp, ArrowDown } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";

export default function MonthlyBalance() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [days, setDays] = useState([]);

  const load = async () => {
    const [y, m] = month.split("-").map(Number);
    const last = new Date(y, m, 0).getDate();
    const results = [];
    for (let d = 1; d <= last; d++) {
      const day = `${month}-${String(d).padStart(2, "0")}`;
      try {
        const { data } = await api.get(`/finance/daily-cash?date=${day}`);
        if (data.count > 0 || data.suprimento > 0 || data.sangria > 0) {
          results.push({ day, ...data });
        }
      } catch {}
    }
    setDays(results);
  };
  useEffect(() => { load(); }, [month]);

  const totals = days.reduce((acc, d) => ({
    sales: acc.sales + d.sales_total,
    suprimento: acc.suprimento + d.suprimento,
    sangria: acc.sangria + d.sangria,
    net: acc.net + d.total,
    count: acc.count + d.count,
  }), { sales: 0, suprimento: 0, sangria: 0, net: 0, count: 0 });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Balanço Mensal"
        subtitle="Consolidado dos fechamentos diários do mês"
        actions={
          <div>
            <Label className="text-xs">Mês</Label>
            <Input type="month" value={month} onChange={(e)=>setMonth(e.target.value)} className="w-44" data-testid="monthly-month-picker"/>
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4 border border-slate-200 bg-gradient-to-br from-rose-50 to-white" data-testid="monthly-net">
          <TrendingUp className="w-5 h-5 text-rose-600 mb-2"/>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Líquido</div>
          <div className="font-heading text-2xl font-bold text-rose-600 mt-1">R$ {totals.net.toFixed(2)}</div>
        </Card>
        <Card className="p-4 border border-slate-200" data-testid="monthly-sales">
          <DollarSign className="w-5 h-5 text-emerald-600 mb-2"/>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Vendas de planos</div>
          <div className="font-heading text-2xl font-bold text-slate-900 mt-1">R$ {totals.sales.toFixed(2)}</div>
          <div className="text-xs text-slate-500">{totals.count} venda(s)</div>
        </Card>
        <Card className="p-4 border border-emerald-200 bg-emerald-50/40">
          <ArrowUp className="w-5 h-5 text-emerald-600 mb-2"/>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Suprimentos</div>
          <div className="font-heading text-2xl font-bold text-emerald-600 mt-1">+ R$ {totals.suprimento.toFixed(2)}</div>
        </Card>
        <Card className="p-4 border border-rose-200 bg-rose-50/40">
          <ArrowDown className="w-5 h-5 text-rose-600 mb-2"/>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Sangrias</div>
          <div className="font-heading text-2xl font-bold text-rose-600 mt-1">- R$ {totals.sangria.toFixed(2)}</div>
        </Card>
      </div>

      <Card className="p-5 border border-slate-200">
        <h3 className="font-heading font-bold text-slate-900 mb-4">Receita diária</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={days.map(d => ({ day: d.day.slice(-2), total: d.total }))}>
              <XAxis dataKey="day" tick={{ fontSize: 11 }}/>
              <YAxis tick={{ fontSize: 11 }}/>
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} formatter={(v)=>`R$ ${v.toFixed(2)}`}/>
              <Bar dataKey="total" fill="#E11D48" radius={[6,6,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="border border-slate-200 overflow-x-auto">
        <table className="w-full min-w-[700px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">Dia</th><th className="px-4 py-3">Vendas</th>
              <th className="px-4 py-3 text-right">Vendido</th><th className="px-4 py-3 text-right">Suprim.</th>
              <th className="px-4 py-3 text-right">Sangria</th><th className="px-4 py-3 text-right">Líquido</th>
              <th className="px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody data-testid="monthly-days-list">
            {days.map(d => (
              <tr key={d.day} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 text-sm font-mono">{d.day.slice(-2)}/{d.day.slice(5,7)}</td>
                <td className="px-4 py-3 text-sm">{d.count}</td>
                <td className="px-4 py-3 text-right font-mono text-sm">R$ {d.sales_total.toFixed(2)}</td>
                <td className="px-4 py-3 text-right font-mono text-sm text-emerald-600">+{d.suprimento.toFixed(2)}</td>
                <td className="px-4 py-3 text-right font-mono text-sm text-rose-600">-{d.sangria.toFixed(2)}</td>
                <td className="px-4 py-3 text-right font-mono font-bold text-rose-600">R$ {d.total.toFixed(2)}</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full font-semibold ${d.closed ? "bg-slate-900 text-white" : "bg-emerald-50 text-emerald-700"}`}>{d.closed ? "Fechado" : "Aberto"}</span></td>
              </tr>
            ))}
            {days.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">Nenhum movimento neste mês</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
